import { AttachmentApiService, BlogApiService, CategoryApiService, FileType, ProductApiService, TagApiService } from '@/core/services';
import { EnableValues } from "@/shared";
import { formControl, FormControlExtended, formGroup } from '@al00x/forms';
import { NgIf } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { APIHelper, ButtonClickEvent, CacBaseDialogComponent, CacButtonComponent, CacDialogLayoutComponent, CacFieldComponent, CacFormBuilderComponent, CacTableComponent, formBuilder, GetOfflineAdapter, SortModel, table } from '@controladad/ng-base';
import { ProductCreate, ProductDetailImage, ProductEntity, ProductFile, ProductImageUpdate as ProductImagePut, ProductUpdate } from '@repo/types';
import { QuillModule } from 'ngx-quill';
import { BehaviorSubject, map } from 'rxjs';

export interface ProductDialogData {
    product?: ProductEntity;
}

export type ProductDialogResult = boolean;


@Component({
    selector: 'feature-product-dialog',
    standalone: true,
    imports: [CacDialogLayoutComponent, CacFormBuilderComponent, CacTableComponent, CacFieldComponent, NgIf, CacButtonComponent, QuillModule, ReactiveFormsModule],
    templateUrl: './product-dialog.component.html',
    styleUrl: './product-dialog.component.scss',
})
export class ProductDialogComponent extends CacBaseDialogComponent<ProductDialogData, ProductDialogResult> {
    private readonly productApi = inject(ProductApiService);
    private readonly tagApi = inject(TagApiService);
    private readonly blogApi = inject(BlogApiService);
    private readonly categoryApi = inject(CategoryApiService);
    private readonly attachmentApi = inject(AttachmentApiService);
    productImages$ = new BehaviorSubject<ProductDetailImage[]>([]);
    relatedProducts$ = new BehaviorSubject<ProductEntity[]>([]);
    isUploadDocumentLoading = signal<boolean>(false);
    isUploadModelLoading = signal<boolean>(false);
    isUploadVideoLoading = signal<boolean>(false);


    basicForm = formBuilder({
        cols: 2,
        inputs: {
            name: {
                control: formControl<string>(undefined, Validators.required),
                label: 'Product Name',
            },
            priority: {
                control: formControl<number>(undefined, Validators.required),
                label: 'Priority',
                inputType: 'number',
            },
            status: {
                control: formControl<boolean>(true, Validators.required),
                label: 'Enable',
                type: 'switch',
                items: EnableValues,
            },
            slug: {
                control: formControl<string>(undefined, Validators.required),
                label: 'Slug',
            },

            partNumber: {
                control: formControl<string | undefined>(undefined),
                label: 'Part Number',
                inputType: 'text',
            },
            material: {
                control: formControl<string | undefined>(undefined),
                label: 'Material',
                inputType: 'text',
            },
            madeIn: {
                control: formControl<string | undefined>(undefined),
                label: 'Made in',
                inputType: 'text',

            },
            tags: {
                control: formControl<number[] | undefined>(undefined, Validators.required),
                type: 'select',
                label: 'Tags',
                multiple: true,
                items: this.tagApi.getAllTags().pipe(map((tags) => tags.map((v) => ({ value: v.id, label: v.name }))))
            },
            categories: {
                control: formControl<number[] | undefined>(undefined, Validators.required),
                type: 'select',
                label: 'Categories',
                multiple: true,
                items: this.categoryApi.getAll().pipe(map((categories) => categories.map((v) => ({ value: v.id, label: v.name }))))
            },
            relatedBlogId: {
                control: formControl<number | undefined>(undefined),
                type: 'select',
                label: 'Blog',
                multiple: false,
                items: this.blogApi.getAllBlogs().pipe(map((blogs) => blogs.map((v) => ({ value: v.id, label: v.title }))))
            },


        },
    });
    color = formControl<string | null>('#000000', Validators.required);
    descriptionsForm = formGroup({
        technicalDescription: formControl<string | undefined>(undefined),
        technicalSpecification: formControl<string | undefined>(undefined),
        otherDescription: formControl<string | undefined>(undefined),
        relatedCars: formControl<string | undefined>(undefined),
    });
    videoForm = formBuilder({
        cols: 2,
        inputs: {
            video: {
                control: formControl<ProductFile | File | undefined>(null),
                label: 'Video',
                type: 'file',

            },
            videoTitle: {
                control: formControl<string | undefined>(undefined),
                label: 'Video Title',
                inputType: 'text',
                clearable: true,

            },

        }
    });
    document = formControl<ProductFile | File | undefined>(null);
    model = formControl<ProductFile | File | undefined>(null);
    relatedProductsTable = table<ProductEntity>({
        itemsFn: GetOfflineAdapter(this.relatedProducts$),
        pagination: false,
        view: {
            title: 'Related Products',
            itemName: 'Related Product',

        },
        columns: {

            id: { label: 'ID' },
            image: { label: 'Image', type: 'image', transform: (v) => v.url },
            name: { label: 'Name', },
        },
        actions: [
            {
                type: 'button',
                content: 'Delete',
                action: (item) => {
                    return this.onRemoveRelatedProducts(item.id);
                },
            },
        ],
        events: {
            add: () => {
                dialog$.productList({ selectedIds: this.relatedProducts$.value.map((x) => x.id) })
                    .afterSubmit()
                    .subscribe((result) => {
                        this.relatedProducts$.next(result.products);
                        this.relatedProductsTable.refresh();
                    });
            },
        }
    });
    imagesTable = table<ProductDetailImage>({
        itemsFn: GetOfflineAdapter(this.productImages$),
        showIndex: false,
        pagination: {
            size: 50,
            autoHide:true,
        },

        view: {
            title: 'Product Images',
            itemName: 'Product Image',
        },
        columns: {
            url: { label: 'Image', type: 'image' },
            priority: { label: 'Priority', type: 'number' },
        },

        actions: [
            {
                type: 'button',
                content: 'Delete',
                action: (item) => {
                    return this.onRemoveImage(item.path!);
                },
            },
        ],
        events:
        {
            add: () => {
                dialog$.productImage().afterSubmit().subscribe((result) => {
                    if (!result) return;
                    const image = result.image;
                    this.productImages$.next([...this.productImages$.getValue(), { url: image.url, priority: image.priority, path: image.path }])
                    this.imagesTable.refresh();
                })
            }
        }
    });


    constructor() {
        super();
        const values = this.data?.product;

        if (values?.id) {
            this.productApi.getDetails(values.id).pipe(takeUntilDestroyed()).subscribe((data) => {

                this.basicForm.patchValue({
                    ...data.product,
                    status: data.product.status == 1 ? true : false,
                    tags: data.product.tags.map(x => x.id),
                    categories: data.product.categories.map(x => x.id),
                })
                this.descriptionsForm.patchValue(data.product);
                if (data.product.colorCode) {
                    this.color.patchValue(data.product.colorCode);
                }

                if (data.product.document) {
                    this.document.patchValue(data.product.document);
                }
                if (data.product.model) {
                    this.model.patchValue(data.product.model);
                }
                if (data.product.video) {
                    this.videoForm.patchValue({
                        video: { ...data.product.video } as ProductFile,
                        videoTitle: data.product.video.title,
                    });
                }
                this.productImages$.next(data.product.images.sort((a, b) => b.priority - a.priority).map((image) => ({ priority: image.priority, url: image.url, path: image.path, })));
                this.relatedProducts$.next(data.product.relatedProducts);
                this.imagesTable.refresh();
                this.relatedProductsTable.refresh();
            })
        }
        this.document.value$.pipe(takeUntilDestroyed()).subscribe((document) => {
            if (document instanceof File) {
                this.isUploadDocumentLoading.set(true);
                this.attachmentApi.upload(document).subscribe((result) => {
                    this.document.patchValue(result);
                    this.isUploadDocumentLoading.set(false);
                });
            }
        });
        this.model.value$.pipe(takeUntilDestroyed()).subscribe((model) => {
            if (model instanceof File) {
                this.isUploadModelLoading.set(true);
                this.attachmentApi.upload(model).subscribe((result) => {
                    this.model.patchValue(result);
                    this.isUploadModelLoading.set(false);
                });
            }
        });

        this.videoForm.controls.video.value$.pipe(takeUntilDestroyed()).subscribe((video) => {
            if (video instanceof File) {
                this.isUploadVideoLoading.set(true)
                this.attachmentApi.upload(video).subscribe((result) => {
                    this.videoForm.controls.video.patchValue(result);
                    this.isUploadVideoLoading.set(false)
                });
            }
        })


    }
    isProductFile(value: unknown): value is ProductFile {
        return typeof value === 'object' && value !== null && 'url' in value;
    }


    openDocumentInNewTab(): void {
        const value = this.document.value;
        if (this.isProductFile(value)) {
            window.open(value.url, '_blank');
        }
    }
    openVideoInNewTab(): void {
        const value = this.videoForm.controls.video.value;
        if (this.isProductFile(value)) {
            window.open(value.url, '_blank');
        }
    }
    openModelInNewTab(): void {

        const value = this.model.value;
        if (this.isProductFile(value)) {
            window.open(value.url, '_blank');
        }
    }
    onRemoveImage(imagePath: string) {
        const images = this.productImages$.value;
        this.productImages$.next(images.filter((x) => x.path !== imagePath));
        this.imagesTable.refresh();
    }
    onRemoveRelatedProducts(id: number) {
        const relatedProducts = this.relatedProducts$.value;
        this.relatedProducts$.next(relatedProducts.filter((x) => x.id !== id));
        this.relatedProductsTable.refresh();
    }
    onAddRelatedProducts() {
        dialog$
            .productList({ selectedIds: this.relatedProducts$.value.map((x) => x.id) })
            .afterSubmit()
            .subscribe((result) => {
                this.relatedProducts$.next(result.products);
                this.relatedProductsTable.refresh();
            });
    }

    get documentOptions() {
        return {
            fileType: 'Product' as FileType,
        };
    }

    onSubmit(e: ButtonClickEvent) {
        const basicValues = this.basicForm.getValue();
        const pId = this.data.product?.id;
        const isLoadingUpload = this.isUploadDocumentLoading() || this.isUploadVideoLoading() || this.isUploadModelLoading();
        if (!basicValues || isLoadingUpload) return;
        if (pId) {
            const params = this.buildProductModel(true) as ProductUpdate;
            this.productApi.updateProduct(params).pipe(e.pipe()).subscribe(() => this.submit(true));

        } else {
            const params = this.buildProductModel(false) as ProductCreate;
            this.productApi.create(params).pipe(e.pipe()).subscribe(() => this.submit(true));
        }
    }

    private buildProductModel(forUpdate: boolean): ProductCreate | ProductUpdate {
        const basicValues = this.basicForm.getValue()!;
        const descriptionsValues = this.descriptionsForm.getRawValue()!;
        const videoValues = this.videoForm.getValue()!;
        const imagesPath: ProductImagePut[] = this.productImages$.getValue().map((image) => ({
            path: image.path!,
            priority: image.priority,
        }));
        const relatedProductIds = this.relatedProducts$.getValue().map((x) => x.id);
        const documentPath = this.getPath(this.document);
        const modelPath = this.getPath(this.model);
        const videoPath = this.getPath(this.videoForm.controls.video);

        const data: any = {
            slug: basicValues.slug,
            id: forUpdate ? this.data?.product?.id! : undefined,
            name: basicValues.name,
            priority: basicValues.priority,
            partNumber: basicValues.partNumber,
            categoryIds: basicValues.categories,
            images: imagesPath,
            madeIn: basicValues.madeIn,
            colorCode: this.color.value,
            material: basicValues.material,
            otherDescription: descriptionsValues.otherDescription,
            relatedBlogId: basicValues.relatedBlogId?.toString(),
            technicalDescription: descriptionsValues.technicalDescription,
            technicalSpecification: descriptionsValues.technicalSpecification,
            relatedCars: descriptionsValues.relatedCars,
            status: basicValues.status ? 1 : 0,
            tagIds: basicValues.tags,
            documentPath: documentPath,
            modelPath: modelPath,
            relatedProductIds: relatedProductIds,
            videoPath: videoPath,
            videoTitle: videoValues.videoTitle,
        };

        return forUpdate ? APIHelper.nestValues(data) : data;
    }

    getPath(control: FormControlExtended): string | null {
        const documentValue = control.value;
        if (this.isProductFile(documentValue)) {
            return documentValue.path;

        }
        return null;
    }


}
